import React, { createContext, ReactNode, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  category: 'veg' | 'non-veg';
  description: string;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: Omit<CartItem, 'quantity'>) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  total: number;
  getItemQuantity: (id: string) => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize cart from localStorage if available
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (item: Omit<CartItem, 'quantity'>) => {
    setCartItems(prev => {
      const existingItem = prev.find(i => i.id === item.id);
      if (existingItem) {
        const newQuantity = existingItem.quantity + 1;
        toast.success(
          newQuantity === 1
            ? `Added ${item.name} to cart`
            : `Added ${newQuantity}${newQuantity === 2 ? 'nd' : newQuantity === 3 ? 'rd' : 'th'} ${item.name} to cart`,
          { id: item.id }
        );
        return prev.map(i =>
          i.id === item.id ? { ...i, quantity: newQuantity } : i
        );
      }
      toast.success(`Added ${item.name} to cart`, { id: item.id });
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const removeFromCart = (id: string) => {
    setCartItems(prev => {
      const item = prev.find(i => i.id === id);
      if (!item) return prev;
      toast.success(`Removed ${item.name} from cart`, { id: `remove-${id}` });
      return prev.filter(item => item.id !== id);
    });
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity < 0) {
      toast.error('Quantity cannot be negative');
      return;
    }

    setCartItems(prev => {
      const item = prev.find(i => i.id === id);
      if (!item) return prev;

      if (quantity === 0) {
        toast.success(`Removed ${item.name} from cart`, { id: `remove-${id}` });
        return prev.filter(i => i.id !== id);
      }

      toast.success(`Updated ${item.name} quantity to ${quantity}`, { id: `update-${id}` });
      return prev.map(item =>
        item.id === id ? { ...item, quantity } : item
      );
    });
  };

  const clearCart = () => {
    setCartItems([]);
    toast.success('Cart cleared');
  };

  const getItemQuantity = (id: string): number => {
    const item = cartItems.find(i => i.id === id);
    return item ? item.quantity : 0;
  };

  const total = cartItems.reduce(
    (sum: number, item: CartItem) => sum + item.price * item.quantity,
    0
  );

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        total,
        getItemQuantity,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};